import React, { type FC, type ReactNode, Suspense, use, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "@apollo/client/react";
import { Button, ButtonGroup, cn } from "@heroui/react";
import { GQL } from "@binale-tech/shared";

import { CompanyContext } from "@domain/context/CompanyContext";
import { UserContext } from "@domain/context/UserProvider";
import { dmsAiUsageStatsQuery } from "@domain/context/queries/dmsAiUsageQueries.graphql";
import { HomeWidgetShell } from "@app/pages/home/HomeWidgetShell";
import { HomeExpandToggle } from "@app/pages/home/HomeExpandToggle";
import type { AiUsageChartPoint } from "./charts/AiUsageBarsChart";
import type { AiUsageStagePoint, AiUsageStageSeries } from "./charts/AiUsageStagesChart";
import "./HomeAiUsageWidget.css";

const AiUsageBarsChart = React.lazy(() =>
    import("./charts/AiUsageBarsChart").then((m) => ({ default: m.AiUsageBarsChart })),
);

const AiUsageStagesChart = React.lazy(() =>
    import("./charts/AiUsageStagesChart").then((m) => ({ default: m.AiUsageStagesChart })),
);

const WINDOW_DAYS = 30;

/** Stack order (bottom to top) and colour token of each cost stage. */
const STAGES: readonly { stage: GQL.IDmsAiUsageStageKind; color: string }[] = [
    { stage: GQL.IDmsAiUsageStageKind.Extraction, color: "var(--metrics-stage-extraction)" },
    { stage: GQL.IDmsAiUsageStageKind.MetroSplit, color: "var(--metrics-stage-metro-split)" },
    { stage: GQL.IDmsAiUsageStageKind.Ocr, color: "var(--metrics-stage-ocr)" },
    { stage: GQL.IDmsAiUsageStageKind.DocumentsToRecords, color: "var(--metrics-stage-documents-to-records)" },
    { stage: GQL.IDmsAiUsageStageKind.TransactionExport, color: "var(--metrics-stage-transaction-export)" },
    {
        stage: GQL.IDmsAiUsageStageKind.TransactionSuggestions,
        color: "var(--metrics-stage-transaction-suggestions)",
    },
];

const SCOPE_ALL = "all";
const SCOPE_COMPANY = "company";

const Skeleton: FC = () => (
    <div
        data-testid="home-ai-usage-skeleton"
        className="HomeAiUsageWidget w-120 rounded-2xl bg-app-surface/50 backdrop-blur-md border border-app-surface/80 shadow-lg p-4"
    >
        <div className="animate-pulse space-y-3">
            <div className="h-3 w-24 rounded bg-app-text/20" />
            <div className="h-3 w-full rounded bg-app-text/10" />
            <div className="h-3 w-full rounded bg-app-text/10" />
            <div className="h-3 w-2/3 rounded bg-app-text/10" />
        </div>
    </div>
);

/** Exact chart heights - a mismatched fallback makes the card jump when the lazy chunk resolves. */
const collapsedChartFallback = <div className="h-[110px] animate-pulse rounded bg-app-text/10" />;
const expandedChartFallback = <div className="h-[160px] animate-pulse rounded bg-app-text/10" />;
const stagesChartFallback = <div className="h-[200px] animate-pulse rounded bg-app-text/10" />;

const Stat: FC<{ label: ReactNode; value: ReactNode; muted?: boolean }> = ({ label, value, muted }) => (
    <div className="flex flex-col">
        <span className="text-xs uppercase tracking-wide opacity-60">{label}</span>
        <span className={cn("font-mono text-lg font-semibold tabular-nums", muted && "opacity-70")}>{value}</span>
    </div>
);

const TokenRow: FC<{ label: ReactNode; value: ReactNode }> = ({ label, value }) => (
    <span className="flex items-baseline justify-between gap-3">
        <span className="opacity-70">{label}</span>
        <span className="font-mono tabular-nums">{value}</span>
    </span>
);

/** Module scope - `WINDOW_DAYS` is constant, so the values object never needs re-creating. */
const windowLabelValues = { days: WINDOW_DAYS };

export const HomeAiUsageWidget: FC = () => {
    const { companyGQL } = use(CompanyContext);
    const userData = use(UserContext);
    const intl = useIntl();

    const [scope, setScope] = useState<string>(SCOPE_ALL);
    const [expanded, setExpanded] = useState(false);
    const toggle = useCallback(() => setExpanded((v) => !v), []);

    const selectAllScope = useCallback(() => setScope(SCOPE_ALL), []);
    const selectCompanyScope = useCallback(() => setScope(SCOPE_COMPANY), []);

    const companyId = scope === SCOPE_COMPANY ? (companyGQL?.id ?? null) : null;

    // The whole query is admin-gated server-side; skip it entirely for everyone
    // else so a non-admin never fires a request that can only 403.
    const isAdmin = Boolean(userData.isAdmin);
    const { data, loading, error } = useQuery(dmsAiUsageStatsQuery, {
        variables: { companyId, days: WINDOW_DAYS },
        skip: !isAdmin,
        fetchPolicy: "cache-first",
    });

    const stats = data?.dmsAiUsageStats;

    const chartData: AiUsageChartPoint[] = useMemo(
        () =>
            (stats?.days ?? []).map((d) => ({
                date: d.date,
                documents: d.totals.documents,
                costUSD: d.totals.costUSD,
            })),
        [stats?.days],
    );

    const stageData: AiUsageStagePoint[] = useMemo(
        () =>
            (stats?.days ?? []).map((d) => ({
                date: d.date,
                ...Object.fromEntries(d.stages.map((s) => [s.stage, s.costUSD])),
            })),
        [stats?.days],
    );
    const stageSeries: AiUsageStageSeries[] = useMemo(
        () =>
            STAGES.filter(({ stage }) => stats?.stageTotals.some((t) => t.stage === stage)).map(({ stage, color }) => ({
                stage,
                color,
                name: intl.formatMessage({ id: `app.home.aiUsage.stage.${stage}` }),
            })),
        [stats?.stageTotals, intl],
    );

    // `days` is dense and ascending, so the recent-N cuts are plain tail slices.
    const recent = useMemo(() => {
        const days = stats?.days ?? [];
        const sumDocs = (from: number) => days.slice(from).reduce((acc, d) => acc + d.totals.documents, 0);
        return {
            today: days.length > 0 ? sumDocs(days.length - 1) : 0,
            week: sumDocs(Math.max(0, days.length - 7)),
        };
    }, [stats?.days]);

    const formatInt = useCallback((v: number | string) => intl.formatNumber(Number(v)), [intl]);
    const formatUSD = useCallback(
        (v: number | string) =>
            intl.formatNumber(Number(v), { style: "currency", currency: "USD", maximumFractionDigits: 2 }),
        [intl],
    );
    // Axis gutter is 40px - keep ticks to a few glyphs.
    const formatIntTick = useCallback((v: number) => intl.formatNumber(v, { notation: "compact" }), [intl]);
    const formatUsdTick = useCallback(
        (v: number) => `$${intl.formatNumber(v, { notation: "compact", maximumFractionDigits: 1 })}`,
        [intl],
    );
    const formatTokens = useCallback(
        (v: number) => intl.formatNumber(v, { notation: "compact", maximumFractionDigits: 1 }),
        [intl],
    );

    const documentsLabel = intl.formatMessage({ id: "app.home.aiUsage.documents" });
    const costLabel = intl.formatMessage({ id: "app.home.aiUsage.cost" });

    if (!isAdmin) {
        return null;
    }
    if (loading && !stats) {
        return <Skeleton />;
    }
    if (error || !stats) {
        return null;
    }

    const header = (
        <div className="flex w-full items-center justify-between gap-4">
            <span className="flex items-baseline gap-3">
                {/* Title leads - the scope switch qualifies it, so it reads
                    "KI-Verarbeitung, for: all / this company". */}
                <span className="text-sm font-bold uppercase tracking-wide opacity-70">
                    <FormattedMessage id="app.home.aiUsage.title" />
                </span>
                {/* Stock ButtonGroup - no custom classes or inline styles. The
                    active scope overrides the group's `outline` with the filled
                    `secondary` variant, and `aria-pressed` is what carries that
                    state to assistive tech, since plain Buttons have no
                    selection of their own. */}
                <ButtonGroup
                    aria-label={intl.formatMessage({ id: "app.home.aiUsage.scope" })}
                    size="sm"
                    variant="outline"
                >
                    <Button
                        variant={scope === SCOPE_ALL ? "secondary" : undefined}
                        aria-pressed={scope === SCOPE_ALL}
                        onPress={selectAllScope}
                    >
                        <FormattedMessage id="app.home.aiUsage.scope.all" />
                    </Button>
                    <Button
                        variant={scope === SCOPE_COMPANY ? "secondary" : undefined}
                        aria-pressed={scope === SCOPE_COMPANY}
                        onPress={selectCompanyScope}
                        isDisabled={!companyGQL?.id}
                    >
                        <ButtonGroup.Separator />
                        <FormattedMessage id="app.home.aiUsage.scope.company" />
                    </Button>
                </ButtonGroup>
            </span>
            <HomeExpandToggle expanded={expanded} onToggle={toggle} />
        </div>
    );

    const statsRow = (
        <div className="mt-3 flex items-end justify-between gap-4">
            <Stat label={<FormattedMessage id="app.home.aiUsage.today" />} value={formatInt(recent.today)} />
            <Stat label={<FormattedMessage id="app.home.aiUsage.week" />} value={formatInt(recent.week)} />
            <Stat
                label={<FormattedMessage id="app.home.aiUsage.window" values={windowLabelValues} />}
                value={formatInt(stats.totals.documents)}
            />
            <Stat
                label={<FormattedMessage id="app.home.aiUsage.cost" />}
                value={formatUSD(stats.totals.costUSD)}
                muted
            />
        </div>
    );

    const collapsedBody = (
        <>
            {statsRow}
            <div className="mt-2">
                <Suspense fallback={collapsedChartFallback}>
                    <AiUsageBarsChart
                        data={chartData}
                        dataKey="documents"
                        color="var(--metrics-series-1)"
                        height={110}
                        formatTick={formatIntTick}
                        formatValue={formatInt}
                        name={documentsLabel}
                    />
                </Suspense>
            </div>
        </>
    );

    const totalTokens =
        stats.totals.inputTokens +
        stats.totals.cachedInputTokens +
        stats.totals.outputTokens +
        stats.totals.thoughtsTokens;

    const expandedBody = (
        <div className="mt-4 grid grid-cols-2 gap-x-8">
            <div className="space-y-5">
                <section>
                    <span className="text-xs font-bold uppercase opacity-60">
                        <FormattedMessage id="app.home.aiUsage.documentsPerDay" />
                    </span>
                    <Suspense fallback={expandedChartFallback}>
                        <AiUsageBarsChart
                            data={chartData}
                            dataKey="documents"
                            color="var(--metrics-series-1)"
                            height={160}
                            formatTick={formatIntTick}
                            formatValue={formatInt}
                            name={documentsLabel}
                        />
                    </Suspense>
                </section>
                <section>
                    <span className="text-xs font-bold uppercase opacity-60">
                        <FormattedMessage id="app.home.aiUsage.costPerDay" />
                    </span>
                    <Suspense fallback={expandedChartFallback}>
                        <AiUsageBarsChart
                            data={chartData}
                            dataKey="costUSD"
                            color="var(--metrics-series-2)"
                            height={160}
                            formatTick={formatUsdTick}
                            formatValue={formatUSD}
                            name={costLabel}
                        />
                    </Suspense>
                </section>
                {stats.companyId && stageSeries.length > 0 && (
                    <section data-testid="home-ai-usage-stages">
                        <span className="text-xs font-bold uppercase opacity-60">
                            <FormattedMessage id="app.home.aiUsage.costByStage" />
                        </span>
                        <Suspense fallback={stagesChartFallback}>
                            <AiUsageStagesChart
                                data={stageData}
                                series={stageSeries}
                                height={200}
                                formatTick={formatUsdTick}
                                formatValue={formatUSD}
                            />
                        </Suspense>
                        <table className="mt-1 w-full text-sm">
                            <tbody>
                                {stats.stageTotals.map((t) => (
                                    <tr key={t.stage}>
                                        <td className="py-0.5 pr-2">
                                            <FormattedMessage id={`app.home.aiUsage.stage.${t.stage}`} />
                                        </td>
                                        <td className="py-0.5 text-right font-mono tabular-nums opacity-70">
                                            {formatInt(t.calls)}
                                        </td>
                                        <td className="py-0.5 text-right font-mono tabular-nums">
                                            {formatUSD(t.costUSD)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}
            </div>
            <div className="space-y-5">
                <section>
                    <span className="text-xs font-bold uppercase opacity-60">
                        <FormattedMessage id="app.home.aiUsage.tokens" />
                    </span>
                    <div className="mt-1 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <TokenRow
                            label={<FormattedMessage id="app.home.aiUsage.tokens.input" />}
                            value={formatTokens(stats.totals.inputTokens)}
                        />
                        <TokenRow
                            label={<FormattedMessage id="app.home.aiUsage.tokens.cached" />}
                            value={formatTokens(stats.totals.cachedInputTokens)}
                        />
                        <TokenRow
                            label={<FormattedMessage id="app.home.aiUsage.tokens.output" />}
                            value={formatTokens(stats.totals.outputTokens)}
                        />
                        <TokenRow
                            label={<FormattedMessage id="app.home.aiUsage.tokens.thoughts" />}
                            value={formatTokens(stats.totals.thoughtsTokens)}
                        />
                        <TokenRow
                            label={<FormattedMessage id="app.home.aiUsage.tokens.total" />}
                            value={formatTokens(totalTokens)}
                        />
                    </div>
                </section>
                <section>
                    <span className="text-xs font-bold uppercase opacity-60">
                        <FormattedMessage id="app.home.aiUsage.models" />
                    </span>
                    <table className="mt-1 w-full text-sm">
                        <thead>
                            <tr className="text-xs uppercase opacity-60">
                                <th className="py-1 text-left font-normal">
                                    <FormattedMessage id="app.home.aiUsage.model" />
                                </th>
                                <th className="py-1 text-right font-normal">
                                    <FormattedMessage id="app.home.aiUsage.documents" />
                                </th>
                                <th className="py-1 text-right font-normal">
                                    <FormattedMessage id="app.home.aiUsage.tokens" />
                                </th>
                                <th className="py-1 text-right font-normal">
                                    <FormattedMessage id="app.home.aiUsage.cost" />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.models.map((m) => (
                                <tr key={m.model || "unknown"}>
                                    <td className="py-0.5 pr-2 font-mono text-xs">
                                        {m.model || <FormattedMessage id="app.home.aiUsage.model.unknown" />}
                                    </td>
                                    <td className="py-0.5 text-right font-mono tabular-nums">
                                        {formatInt(m.totals.documents)}
                                    </td>
                                    <td className="py-0.5 text-right font-mono tabular-nums opacity-70">
                                        {formatTokens(
                                            m.totals.inputTokens +
                                                m.totals.cachedInputTokens +
                                                m.totals.outputTokens +
                                                m.totals.thoughtsTokens,
                                        )}
                                    </td>
                                    <td className="py-0.5 text-right font-mono tabular-nums">
                                        {formatUSD(m.totals.costUSD)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
                <p className="text-xs leading-snug opacity-50">
                    <FormattedMessage id="app.home.aiUsage.hint" values={{ from: stats.from, to: stats.to }} />
                </p>
            </div>
        </div>
    );

    return (
        <HomeWidgetShell
            className="HomeAiUsageWidget"
            expanded={expanded}
            // Leftmost column, so the panel pins its left edge and grows right
            // over the accounting widgets rather than off the viewport.
            anchor="left"
            expandedWidth="w-[800px]"
            header={header}
            collapsed={collapsedBody}
        >
            {expandedBody}
        </HomeWidgetShell>
    );
};
